import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { UsableChatEmbed, ParentToolSchema, GeneratedImagePayload } from "./embed-sdk";
import { ChatHistoryStore, DEFAULT_CONVERSATION_TITLE } from "./history-store";
import { ToolCallCapabilities } from "./image-attachment";
import { GraphAuth, graphConsentToolResult } from "./graph-auth";

const DEFAULT_IFRAME_ORIGIN = "https://chat.usable.dev";

/** Context-entry key under which the current-focus snapshot is injected. */
const CURRENT_CONTEXT_KEY = "current_context";
/** Debounce for context refresh — selection-change events can fire rapidly. */
const CONTEXT_REFRESH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Config (host supplies these; the hook stays host-agnostic)
// ---------------------------------------------------------------------------

/**
 * A host-specific source of "what the user is currently looking at" — the
 * selected range, open email, current slide, etc. Kept host-agnostic here: the
 * Office.js reading lives in each app (`apps/<host>/.../<host>-context.ts`); the
 * hook only calls `read()` and re-injects on `subscribe`. The returned text is
 * pushed as a runtime context entry so the assistant resolves "fix this" /
 * "what's wrong here" without the user pasting anything.
 */
export interface HostContextProvider {
  /** Read a concise snapshot of the current focus. Null/empty → no context entry. */
  read: () => Promise<string | null>;
  /**
   * Subscribe to host changes (selection/item switch) that should refresh the
   * snapshot; call `onChange` on each, return an unsubscribe fn. Optional.
   */
  subscribe?: (onChange: () => void) => () => void;
}

export interface ChatEmbedConfig {
  /** Public embed token — selects which Usable workspace/expert the iframe shows. */
  embedToken: string;
  /** Parent tool schemas registered with the embed on READY. */
  toolSchemas: ParentToolSchema[];
  /**
   * Handle a tool call from the embed; resolve with the tool result. The third
   * argument exposes opt-in host capabilities (the embed's `addImage`, the
   * requestId) — most hosts ignore it; PowerPoint uses it to deliver slide
   * screenshots as chat attachments.
   */
  onToolCall: (tool: string, args: unknown, caps: ToolCallCapabilities) => Promise<unknown>;
  /**
   * Opt-in Microsoft Graph token provider (Outlook only). When present, its
   * `acquireGraphToken` is injected into every tool call's `caps`, and a
   * `GraphConsentRequired` thrown from a handler is mapped to a structured
   * `{ status: "interaction_required" }` result instead of surfacing as an error.
   */
  graphAuth?: GraphAuth;
  /** Per-app local chat-history store (created with the app's dbName). */
  historyStore: ChatHistoryStore;
  /** Expected iframe origin. Defaults to https://chat.usable.dev. */
  iframeOrigin?: string;
  /** Optional tap on every validated embed message + capture diagnostics (feeds a dev inspector). */
  onMessage?: (type: string, payload: unknown) => void;
  /**
   * Optional host source of the current document/selection/email, injected as a
   * runtime context entry on READY and refreshed on selection changes. When
   * absent, the embed simply gets no ambient document context (prior behaviour).
   */
  contextProvider?: HostContextProvider;
  /**
   * Optional handler for AI-generated images pushed from the embed
   * (IMAGE_GENERATED). Hosts use it to cache the bytes so document tools can
   * insert "the image you just generated" without any cross-origin fetch.
   */
  onImageGenerated?: (payload: GeneratedImagePayload) => void;
}

/** Trailing-debounce a zero-arg callback. */
function debounce(fn: () => void, ms: number): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, ms);
  };
}

// ---------------------------------------------------------------------------
// Reactive + imperative surface for the history panel
// ---------------------------------------------------------------------------

export interface ChatEmbedApi {
  /** JWT `sub` of the signed-in user — scopes every local-history read. */
  userId: string | null;
  /** Conversation currently shown in the embed (for list highlighting). */
  activeConversationId: string | null;
  /** Bumps whenever the embed writes to the local store, so the panel re-reads. */
  storeVersion: number;
  /** Re-hydrate the embed with a stored conversation. */
  switchConversation: (conversationId: string) => Promise<void>;
  /** Start a fresh conversation in the embed and clear the restore pointer. */
  newConversation: () => void;
  /**
   * Register additional parent tools at runtime (e.g. the local-folder tools
   * when the user attaches a folder). Sends the FULL merged set (app tools ⊕
   * extras) — correct whether the iframe's register semantics are replace or
   * upsert. No-op before READY.
   */
  registerExtraTools: (schemas: ParentToolSchema[]) => void;
  /**
   * Remove runtime-registered tools by name, then re-register the app's base
   * set (covers both replace and upsert iframe semantics). No-op before READY.
   */
  deregisterExtraTools: (toolNames: string[]) => void;
  /**
   * Set (content) or remove (null) ONE named runtime context entry — e.g. the
   * attached-folder announcement. Never touches the hook-owned
   * `current_context` entry.
   */
  setContextEntry: (key: string, content: string | null) => void;
}

/**
 * Decode the `sub` (user id) claim from a JWT without verifying it — used only
 * to scope local history per user, never for trust decisions. Null on any
 * malformed token.
 */
function getUserIdFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const data = JSON.parse(atob(padded));
    return typeof data.sub === "string" ? data.sub : null;
  } catch {
    return null;
  }
}

/**
 * Re-hydrate the embed's transcript from a locally-stored conversation. Shared
 * by restore-on-load (onReady) and the panel's switch action: a `replace-all`
 * upsert swaps the whole transcript and the embed adopts the conversation id,
 * so subsequent turns continue it.
 */
async function restoreConversationIntoEmbed(
  embed: UsableChatEmbed,
  store: ChatHistoryStore,
  conversationId: string,
  log?: (type: string, payload: unknown) => void
): Promise<void> {
  const { messages } = await store.loadConversation(conversationId);
  log?.("restore→upsert", { conversationId, count: messages.length });
  embed.upsertConversationMessages({
    conversationId,
    messages: messages.map((m) => m.message), // raw ExportedMessage[]
    mode: "replace-all",
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Wire a chat iframe to the Usable embed with local chat-history capture/restore.
 * Host-agnostic — the host supplies its embed token, tools, handler, and store.
 *
 * @param iframeRef        - Ref to the chat iframe element.
 * @param accessToken      - Keycloak JWT to authenticate the embed. Null when unauthenticated.
 * @param ensureValidToken - Returns the current token if fresh (>60s), otherwise refreshes.
 * @param config           - Per-app embed config (token, tools, handler, history store).
 */
export function useChatEmbed(
  iframeRef: RefObject<HTMLIFrameElement>,
  accessToken: string | null,
  ensureValidToken: () => Promise<string | null>,
  config: ChatEmbedConfig
): ChatEmbedApi {
  const embedRef = useRef<UsableChatEmbed | null>(null);

  const accessTokenRef = useRef<string | null>(accessToken);
  accessTokenRef.current = accessToken;

  const configRef = useRef<ChatEmbedConfig>(config);
  configRef.current = config;

  // User id (JWT sub) for scoping local history; stable across token refreshes.
  const userId = getUserIdFromToken(accessToken);
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [storeVersion, setStoreVersion] = useState(0);
  const bumpStoreVersion = useCallback(() => setStoreVersion((v) => v + 1), []);

  // -------------------------------------------------------------------------
  // Create / destroy the embed instance when the iframe mounts
  // -------------------------------------------------------------------------

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const cfg = configRef.current;
    const store = cfg.historyStore;
    const log = cfg.onMessage;
    const iframeOrigin = cfg.iframeOrigin ?? DEFAULT_IFRAME_ORIGIN;

    // Read embed token override from roaming settings (if set by the user).
    let embedToken = cfg.embedToken;
    try {
      const override = Office.context.roamingSettings.get("embedTokenOverride") as string | null;
      if (override) embedToken = override;
    } catch {
      // roamingSettings may not be available in all contexts
    }

    const targetSrc = `${iframeOrigin}/embed?token=${encodeURIComponent(embedToken)}`;
    if (iframe.src !== targetSrc) {
      iframe.src = targetSrc;
    }

    const embed = new UsableChatEmbed(iframe, {
      iframeOrigin,

      onMessage: log,

      // Thread the live embed's opt-in capabilities (addImage, staged data,
      // attachments, workspace files, Graph token) + the requestId into the app
      // handler. Excel/Word ignore them; PowerPoint delivers slide screenshots
      // through addImage; Outlook uses acquireGraphToken.
      //
      // A GraphConsentRequired thrown from a handler (the user hasn't connected
      // Microsoft 365) is mapped HERE — once, for every host — to a structured
      // interaction_required result so the model relays the canned "open the menu
      // and click Connect Microsoft 365" sentence instead of seeing a raw auth error.
      onToolCall: async (tool, args, requestId) => {
        const graphAuth = configRef.current.graphAuth;
        try {
          return await configRef.current.onToolCall(tool, args, {
            addImage: (base64OrDataUrl, options) => embed.addImage(base64OrDataUrl, options),
            // Lazy via the ref — referencing `embed.data` directly here would make
            // `embed` reference itself in its own initializer (TS7022).
            stagedData: {
              list: () => embedRef.current!.data.list(),
              readBytes: (alias: string) => embedRef.current!.data.readBytes(alias),
            },
            attachments: {
              list: () => embedRef.current!.attachments.list(),
              readBytes: (fileId: string) => embedRef.current!.attachments.readBytes(fileId),
            },
            workspaceFiles: {
              readBytes: (fileId: string) => embedRef.current!.workspaceFiles.readBytes(fileId),
              putBytes: (fileName: string, mimeType: string, base64: string) =>
                embedRef.current!.workspaceFiles.putBytes(fileName, mimeType, base64),
            },
            requestId,
            acquireGraphToken: graphAuth
              ? (scopes, options) => graphAuth.acquireGraphToken(scopes, options)
              : undefined,
          });
        } catch (err) {
          const consent = graphConsentToolResult(err);
          if (consent) return consent;
          throw err;
        }
      },

      onTokenRefreshRequired: ensureValidToken,

      onError: (code, message) => {
        console.error(`[UsableEmbed] Error ${code}: ${message}`);
      },

      onConversationChange: (conversationId) => setActiveConversationId(conversationId),

      // Capture chat into the local IndexedDB store (idempotent, userId-scoped).
      onConversationCreated: (p) => {
        log?.("cb:CONVERSATION_CREATED", p);
        const uid = userIdRef.current;
        if (!uid) return;
        void store
          .upsertConversation({
            id: p.conversationId,
            userId: uid,
            title: p.title,
            createdAt: p.createdAt,
            updatedAt: p.updatedAt,
          })
          .then(bumpStoreVersion)
          .catch((e) => log?.("store✗", { op: "upsertConversation", error: String(e) }));
      },
      onMessageCreated: (p) => {
        log?.("cb:MESSAGE_CREATED", p);
        const uid = userIdRef.current;
        if (!uid) return;
        void (async () => {
          try {
            const { inserted } = await store.appendMessage({
              userId: uid,
              conversationId: p.conversationId,
              kind: p.kind,
              message: p.message,
            });
            await store.setLastActive(uid, p.conversationId);
            log?.("store✓", {
              wrote: inserted ? "insert" : "update(dedup)",
              conversationId: p.conversationId,
            });
            bumpStoreVersion();
          } catch (e) {
            // An IndexedDB failure must not be silent — surface it to the inspector.
            log?.("store✗", { op: "appendMessage", conversationId: p.conversationId, error: String(e) });
          }
        })();
      },
      onConversationRenamed: (p) => {
        log?.("cb:CONVERSATION_RENAMED", p);
        const uid = userIdRef.current;
        if (!uid) return;
        void store
          .renameConversation(uid, p.conversationId, p.title ?? DEFAULT_CONVERSATION_TITLE)
          .then(bumpStoreVersion)
          .catch((e) => log?.("store✗", { op: "renameConversation", error: String(e) }));
      },
      onImageGenerated: (p) => {
        log?.("cb:IMAGE_GENERATED", { fileId: p.fileId, mimeType: p.mimeType, bytes: p.base64?.length ?? 0 });
        configRef.current.onImageGenerated?.(p);
      },

      onConversationMessagesUpserted: (p) => {
        log?.("cb:MESSAGES_UPSERTED", p);
        if (!p.ok) {
          console.error(`[UsableEmbed] Restore failed: ${p.error ?? "unknown error"}`);
          return;
        }
        const uid = userIdRef.current;
        if (uid && p.conversationId) {
          void store.setLastActive(uid, p.conversationId);
        }
      },
    });

    embedRef.current = embed;

    // Unsubscribe handle for host context-change events (set up on READY below).
    let unsubscribeContext: (() => void) | null = null;

    // On READY: register tools, send auth, restore the last conversation, and
    // start injecting the current document/selection as runtime context.
    embed.onReady(() => {
      embed.registerTools(configRef.current.toolSchemas);
      if (accessTokenRef.current) {
        embed.setAuth(accessTokenRef.current);
      }

      const uid = userIdRef.current;
      if (uid) {
        void (async () => {
          const lastId = await store.getLastActive(uid);
          if (!lastId) return; // nothing to restore — fresh start
          await restoreConversationIntoEmbed(embed, store, lastId, log);
        })();
      }

      // Ambient "what the user is looking at" context. Failures here must never
      // break tools/auth/restore — read is guarded and independent of the above.
      const provider = configRef.current.contextProvider;
      if (provider) {
        const pushContext = () => {
          void provider
            .read()
            .then((content) => {
              if (!embedRef.current) return; // unmounted mid-read
              if (content && content.trim()) {
                embed.setContextEntries([{ key: CURRENT_CONTEXT_KEY, content }]);
                log?.("context→set", { chars: content.length });
              } else {
                embed.removeContextEntries([CURRENT_CONTEXT_KEY]);
              }
            })
            .catch((e) => log?.("context✗", { error: String(e) }));
        };

        pushContext(); // initial snapshot
        unsubscribeContext =
          provider.subscribe?.(debounce(pushContext, CONTEXT_REFRESH_DEBOUNCE_MS)) ?? null;
      }
    });

    return () => {
      if (unsubscribeContext) unsubscribeContext();
      embed.destroy();
      embedRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef]);

  // -------------------------------------------------------------------------
  // Re-send auth whenever the token changes after READY (silent refresh, etc.)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (accessToken && embedRef.current) {
      ensureValidToken().then((validToken) => {
        if (validToken && embedRef.current) {
          embedRef.current.setAuth(validToken);
        }
      });
    }
  }, [accessToken, ensureValidToken]);

  // -------------------------------------------------------------------------
  // Imperative API for the history panel
  // -------------------------------------------------------------------------

  const switchConversation = useCallback(async (conversationId: string) => {
    const embed = embedRef.current;
    const uid = userIdRef.current;
    if (!embed || !uid) return;
    const store = configRef.current.historyStore;
    setActiveConversationId(conversationId); // optimistic; embed confirms via CONVERSATION_CHANGED
    await restoreConversationIntoEmbed(embed, store, conversationId, configRef.current.onMessage);
    await store.setLastActive(uid, conversationId);
  }, []);

  const newConversation = useCallback(() => {
    const embed = embedRef.current;
    if (!embed) return;
    embed.newConversation();
    setActiveConversationId(null);
    const uid = userIdRef.current;
    if (uid) void configRef.current.historyStore.setLastActive(uid, null);
  }, []);

  // -------------------------------------------------------------------------
  // Runtime tool + context imperatives (used by shell features like the
  // parent-served Local Folder)
  // -------------------------------------------------------------------------

  const registerExtraTools = useCallback((schemas: ParentToolSchema[]) => {
    const embed = embedRef.current;
    if (!embed) return;
    // Full merged set — see ChatEmbedApi doc for the replace/upsert rationale.
    embed.registerTools([...configRef.current.toolSchemas, ...schemas]);
  }, []);

  const deregisterExtraTools = useCallback((toolNames: string[]) => {
    const embed = embedRef.current;
    if (!embed) return;
    embed.deregisterTools(toolNames);
    embed.registerTools(configRef.current.toolSchemas);
  }, []);

  const setContextEntry = useCallback((key: string, content: string | null) => {
    const embed = embedRef.current;
    if (!embed) return;
    if (content && content.trim()) {
      embed.setContextEntries([{ key, content }]);
    } else {
      embed.removeContextEntries([key]);
    }
  }, []);

  return {
    userId,
    activeConversationId,
    storeVersion,
    switchConversation,
    newConversation,
    registerExtraTools,
    deregisterExtraTools,
    setContextEntry,
  };
}
